import React from 'react';
import { Multiselect, SelectProps } from '@cloudscape-design/components';
import { memo, useMemo } from 'react';
import { Category } from 'src/types/helios/Category';

interface QuestionCategoryDropdownProps {
  categories: Category[];
  isLoading?: boolean;
  error?: string | null;
  selectedCategory?: string[] | null;
  onChange: (selectedCategories: string[] | null) => void;
  disabled?: boolean;
}

const QuestionCategoryDropdown = ({
  categories,
  isLoading = false,
  error = null,
  selectedCategory = null,
  onChange,
  disabled = false,
}: QuestionCategoryDropdownProps) => {
  // Memoize the status type calculation
  const statusType = useMemo(() => {
    if (isLoading) return 'loading';
    if (error) return 'error';
    return 'finished';
  }, [isLoading, error]);

  // Memoize the categories list conversion
  const categoryOptions = useMemo(() => {
    return categories.map((category) => ({
      value: category.categoryName,
      label: category.categoryName,
    }));
  }, [categories]);

  // Find the selected options
  const selectedOptions = useMemo(() => {
    if (
      !selectedCategory ||
      !Array.isArray(selectedCategory) ||
      selectedCategory.length === 0
    )
      return [];
    return categoryOptions.filter((option) => selectedCategory.includes(option.value));
  }, [selectedCategory, categoryOptions]);

  return (
    <Multiselect
      data-testid="question-category-select"
      selectedOptions={selectedOptions}
      onChange={({ detail }) => {
        const selectedValues = detail.selectedOptions
          .map((option) => option.value)
          .filter((value): value is string => value !== undefined);
        onChange(selectedValues.length > 0 ? selectedValues : null);
      }}
      options={categoryOptions}
      errorText={error || undefined}
      loadingText="Loading question categories"
      statusType={statusType}
      placeholder="Select categories"
      disabled={disabled}
      expandToViewport
    />
  );
};

// Memoize the entire component for performance
export default memo(QuestionCategoryDropdown, (prevProps, nextProps) => {
  // Custom comparison function
  return (
    prevProps.categories === nextProps.categories &&
    prevProps.selectedCategory === nextProps.selectedCategory &&
    prevProps.isLoading === nextProps.isLoading &&
    prevProps.error === nextProps.error &&
    prevProps.disabled === nextProps.disabled &&
    prevProps.onChange === nextProps.onChange
  );
});
